import { createHead, type Unhead } from '@unhead/vue'
import type { Router, RouterHistory } from 'vue-router'
import {
    createRouter,
    ROUTE_DTL_REPORT,
    ROUTE_ENGAGEMENT_RING,
    ROUTE_ENGAGEMENT_RING_GEMSTONE,
    ROUTE_ENGAGEMENT_RING_LAB_GROWN,
    ROUTE_ENGAGEMENT_RING_STONE_RECOMMENDATIONS,
    ROUTE_ENGAGEMENT_RINGS,
    ROUTE_ENGAGEMENT_RINGS_HOME,
    ROUTE_GEMSTONE_SEARCH,
    ROUTE_JEWELLERIES,
    ROUTE_JEWELLERY,
    ROUTE_LAB_GROWN_DIAMOND_SEARCH,
    ROUTE_NATURAL_DIAMOND_SEARCH,
    ROUTE_NON_SKU_GEMSTONE_SEARCH,
    ROUTE_NON_SKU_LAB_GROWN_DIAMOND_SEARCH,
    ROUTE_PROFILE_MY_ORDERS,
    ROUTE_QUESTIONNAIRE,
    ROUTE_READY_TO_GO_RING,
    ROUTE_READY_TO_GO_RINGS,
    ROUTE_WEDDING_RING,
    ROUTE_WEDDING_RINGS,
    ROUTE_WEDDING_RINGS_HOME,
} from './router'
import {
    type App as VueApp,
    type ComponentPublicInstance,
    type DirectiveBinding,
    inject,
    type Ref,
    ref,
} from 'vue'
import { logVueError } from './helpers/error'
import * as Sentry from '@sentry/vue'
import {
    type Client as GraphqlClient,
    createClient,
    type Exchange,
    fetchExchange,
    install,
    mapExchange,
    ssrExchange as createSSRExchange,
} from '@urql/vue'
import { addHeaderExchange } from '@/graphql/urql/exchange'
import { persistedExchange } from '@urql/exchange-persisted'
import { type AppI18nPlugin, createI18nPlugin } from '@/i18n'
import { installRegionPlugin } from '@/plugins/region'

import '@/styles/vendor/reset.scss'
import { installCurrencyPlugin } from '@/plugins/currency'
import {
    type AffirmConfig,
    type ApiUrls,
    type CookieProvider,
    type IntercomConfig,
    type MetaConfig,
    type SentryConfig,
} from '@/entry-server-types'
import { createPinia, type Pinia, type StateTree } from 'pinia'
import { createPersistedState } from 'pinia-plugin-persistedstate'
import {
    installMetaRobotsPlugin,
    type MetaRobotsPlugin,
} from '@/plugins/robots'
import { installMenuPlugin } from '@/plugins/menu'
import { installRegistryPlugin } from '@/helpers/registry'
import { provideCheckoutComApiUrl } from '@/plugins/checkout-com'
import { createEmptyStorage } from '@/stores'
import { provideHost } from '@/plugins/host'
import { installUserAgent } from '@/plugins/user-agent'
import { tooltipDirective } from './directives/tooltip/tooltip'
import {
    type GraphCacheConfig,
    RingBuilderSetDocument,
    type RingBuilderSetMutation,
    type RingBuilderSetMutationVariables,
} from '@/graphql/api'
import { type GraphCacheKeysConfig } from '@/graphql/api'
import { useGtmVar } from './composables/app/gtm/useGtmVar'
import {
    installRingBuilder,
    provideRingBuilderDataStore,
} from '@/composables/useRingBuilder'
import {
    installReleasedUrls,
    type ReleasedUrlsChecker,
} from './plugins/released-urls/released-urls'
import { provideResetApiClient } from './composables/app/useResetApiClient'
import { provideMsClarityId } from './composables/app/clarity/useMsClarityId'
import { getWindow } from '@/helpers/globals'
import { provideGoogleSignInId } from './plugins/google-sign-in-id'
import { installRoutesPlugin, type Routes } from '@/router/routes'
import {
    type AbsoluteUrlGenerator,
    installAbsoluteUrl,
} from '@/composables/app/useAbsoluteUrl'
import { installIntercom } from '@/plugins/intercom'
import { cacheExchange } from '@urql/exchange-graphcache'
import schema from '@/graphql/api/api.schema'
import { installAnalytics } from '@/composables/app/gtm/useSegment'
import { provideFouanalyticsConfig } from '@/plugins/fouanalytics'
import { provideUrl } from '@/plugins/current-url.ts'
import { installCookieStore } from '@/plugins/cookie-store.ts'
import { installMoneyFormatter } from '@/plugins/money-formatter.ts'
import { installCurrencyInputPlugin } from '@/plugins/currency-input.ts'
import {
    adaptMutation,
    type SimpleMutationResponse,
} from '@/helpers/mutation-adapter.ts'
import { installRouterNavigation } from '@/plugins/released-urls/useRouterNavigation.ts'
import { installReactiveCookiePlugin } from '@/plugins/cookie-plugin'
import { installRingBuilderStore } from '@/plugins/ring-builder-store.ts'
import { trackGtmEvent } from '@/composables/app/gtm/event.ts'
import { installIOS } from '@/plugins/is-ios.ts'
import { installAuth } from '@/plugins/auth.ts'
import { installAffirm } from '@/plugins/affirm/affirm.ts'

export interface FrontOfficeApp {
    app: VueApp
    graphqlClient: Ref<GraphqlClient>
    router: Router
    head: Unhead<{}>
    i18n: AppI18nPlugin
    locale: Ref<string>
    ssrExchange: SSRExchange
    absoluteUrlGenerator: AbsoluteUrlGenerator
    isReleasedUrl: ReleasedUrlsChecker
    // piniaStorage: PiniaStoragePlugin
    pinia: Pinia
    routes: Routes
    // cookieHeader: { cookie: string }
    // entry-client.ts-specific
    // headObject: ComputedRef<HeadObjectPlain>
    // headUpdatesAllowed: ComputedRef<boolean>
    robots: MetaRobotsPlugin
}

export type InitialState = {
    api: SSRData
    pinia: Record<string, StateTree>
}

export interface AppConfig {
    history: RouterHistory
    initialState: InitialState
    urls: ApiUrls
    analyticsId: string | undefined
    googleSignInId: string | undefined
    msClarityId: string | undefined
    checkoutComApiUrl: string | undefined
    sentry: SentryConfig | undefined
    intercom: IntercomConfig | undefined
    affirm: AffirmConfig | undefined
    host: URL
    userAgent: string | undefined
    cookieProvider: CookieProvider
    meta: MetaConfig | undefined
    // eslint-disable-next-line no-restricted-globals
    fetch: typeof fetch
    exchanges: Exchange[]
    qaIsNoJs: boolean | undefined
    founanalytics: {
        enabled: boolean
    }
}

export function createInitialState(): InitialState {
    return {
        api: {},
        pinia: {},
    }
}

const initialStateKey = 'app.state.initial'

export type SSRExchange = ReturnType<typeof createSSRExchange>
export type SSRData = ReturnType<SSRExchange['extractData']>

export function useInitialState(): InitialState {
    return inject(initialStateKey) as InitialState
}

export function createApp(
    app: VueApp,
    {
        history,
        initialState,
        urls,
        msClarityId,
        checkoutComApiUrl,
        sentry,
        host,
        userAgent,
        meta,
        cookieProvider,
        analyticsId,
        googleSignInId,
        intercom,
        affirm,
        fetch,
        exchanges,
        qaIsNoJs,
        founanalytics,
    }: AppConfig
): FrontOfficeApp {
    provideHost(app, host)
    provideGoogleSignInId(app, googleSignInId)
    installAffirm(app, affirm)

    app.config.errorHandler = function (
        err: unknown,
        vm: ComponentPublicInstance | null,
        info: string
    ) {
        logVueError(err, vm, info)
    }

    app.config.performance = import.meta.env.DEV

    const sentryEnabled = !!sentry
    if (!import.meta.env.SSR && sentryEnabled) {
        Sentry.init({
            app,
            dsn: sentry?.dsn,
            environment: sentry?.environment || 'staging',
            integrations: [
                Sentry.replayIntegration({
                    unblock: ['.sentry-unblock, [data-sentry-unblock]'],
                    unmask: ['.sentry-unmask, [data-sentry-unmask]'],
                }),
                Sentry.captureConsoleIntegration({
                    // stupid sentry, no union but strings, dafuq => ['log', 'info', 'warn', 'error', 'debug', 'assert']
                    levels: ['error'],
                }),
            ],
            tracesSampleRate: 0.01,
            tracePropagationTargets: [urls.api],
            replaysSessionSampleRate: 0,
            replaysOnErrorSampleRate: 1.0,
            denyUrls: [/chrome-extension:\/\//],
            ignoreErrors: [
                'TypeError: Failed to fetch',
                'TypeError: NetworkError when attempting to fetch resource.',
                'TypeError: cancelled',
                /Importing a module script failed/,
                /Failed to fetch dynamically imported module/,
                'TypeError: Load failed',
                /Unable to preload CSS/,
            ],
        })
    }

    provideMsClarityId(app, msClarityId)
    provideCheckoutComApiUrl(app, checkoutComApiUrl)

    provideFouanalyticsConfig(app, founanalytics)

    app.provide(initialStateKey, initialState)

    // IF CLIENT APPLY INITIAL STATE TO PINIA

    const head = createHead()
    app.use(head)

    const pinia = createPinia()
    if (!import.meta.env.SSR) {
        // Let it default to localStorage
        pinia.use(createPersistedState())
    } else {
        // Use empty storage by default
        // Stores that will persist via cookies will pass their own storage instance
        pinia.use(
            createPersistedState({
                storage: createEmptyStorage(),
            })
        )
    }
    app.use(pinia)

    const router = createRouter(history)
    provideUrl(app, router, host)

    const absoluteUrlGenerator = installAbsoluteUrl(
        app,
        host.host,
        host.protocol,
        router
    )

    const window = getWindow()

    if (window) {
        router.onError((error, to) => {
            if (
                error.message.includes(
                    'Failed to fetch dynamically imported module'
                ) ||
                error.message.includes('Importing a module script failed')
            ) {
                if (!to?.fullPath) {
                    window.location.reload()
                } else {
                    window.location = to.fullPath
                }
            }
        })
    }

    app.use(router)

    const appTranslator = createI18nPlugin(app, {
        defaultLocale: 'en-gb',
        fallbackLocale: 'en-gb',
    })
    const { i18n, locale, getCountryName, setLocale } = appTranslator
    app.use(i18n)

    app.config.globalProperties.$t = i18n.global.t

    if (!import.meta.env.SSR) {
        pinia.state.value = initialState.pinia || {}
    }

    app.directive('tooltip', tooltipDirective)

    const cookieStore = installCookieStore(app)
    const cookies = installReactiveCookiePlugin(
        app,
        cookieProvider,
        cookieStore
    )
    const regionPlugin = installRegionPlugin(app, {
        getCountryName,
        setLocale,
        cookies,
    })
    const currencyPlugin = installCurrencyPlugin(app, {
        locale,
        cookies,
        regionPlugin,
    })

    const auth = installAuth(app)

    installIntercom(app, cookies, auth, intercom?.app_id)

    installMenuPlugin(app)
    installRegistryPlugin(app)
    installUserAgent(app, userAgent)
    installIOS(app, userAgent)
    const routes = installRoutesPlugin(app, regionPlugin)

    const robots = installMetaRobotsPlugin(app, {
        default: {
            index: meta?.index || true,
            follow: meta?.follow || true,
        },
    })

    const currencyInput = installCurrencyInputPlugin(
        app,
        regionPlugin,
        currencyPlugin
    )

    const ringBuilderDataStore = provideRingBuilderDataStore(app)
    const moneyFormatterNew = installMoneyFormatter(app, appTranslator)

    // installPageMeta(
    //     app,
    //     computed(() => {
    //         return {
    //             title: i18n.global.t('globals.default_meta_title'),
    //             description: i18n.global.t('globals.default_meta_description'),
    //         }
    //     })
    // )

    // const { updatesAllowed } = pageMeta

    // const headObject: ComputedRef<HeadObjectPlain> = computed(() => {
    //     return {
    //         htmlAttrs: {
    //             lang: locale.value,
    //         },
    //         title: pageMeta.title.value,
    //         meta: [
    //             {
    //                 name: 'description',
    //                 content: pageMeta.description.value,
    //             },
    //             {
    //                 name: 'robots',
    //                 content: robots.content.value,
    //             },
    //             ...pageMeta.meta.value,
    //         ],
    //     }
    // })

    // head.addHeadObjs(headObject)

    const releasedRoutes: string[] = [
        ROUTE_PROFILE_MY_ORDERS,
        ROUTE_READY_TO_GO_RINGS,
        ROUTE_READY_TO_GO_RING,
        ROUTE_JEWELLERIES,
        ROUTE_JEWELLERY,
        ROUTE_WEDDING_RINGS_HOME,
        ROUTE_WEDDING_RINGS,
        ROUTE_WEDDING_RING,
        ROUTE_QUESTIONNAIRE,
        ROUTE_ENGAGEMENT_RING,
        ROUTE_ENGAGEMENT_RINGS,
        ROUTE_ENGAGEMENT_RING_GEMSTONE,
        ROUTE_ENGAGEMENT_RINGS_HOME,
        ROUTE_ENGAGEMENT_RING_LAB_GROWN,
        ROUTE_ENGAGEMENT_RING_STONE_RECOMMENDATIONS,
        ROUTE_NATURAL_DIAMOND_SEARCH,
        ROUTE_LAB_GROWN_DIAMOND_SEARCH,
        ROUTE_DTL_REPORT,
        ROUTE_NON_SKU_GEMSTONE_SEARCH,
        ROUTE_NON_SKU_LAB_GROWN_DIAMOND_SEARCH,
        ROUTE_GEMSTONE_SEARCH,
    ]
    const releasedRoutesNext: string[] = []

    const releasedUrls: RegExp[] = [
        /\/profile\/my-orders/,
        /\/ready-to-go(\/.*)?$/,
        /\/jewellery(\/.*)?$/,
        /\/wedding-rings(\/.*)?$/,
        /\/engagement-rings(\/.*)?$/,
        /\/diamonds(\/.*)?$/,
        /\/gemstones(\/.*)?$/,
        /\/ring-guidance(\/.*)?$/,
        /\/diamond-guidance(\/.*)?$/,
        /\/about-us\/payment-options/,
        /\/about-us\/annual-aftercare/,
        /\/about-us\/lifetime-manufacturer-warranty/,
        /\/about-us\/resizing-policy/,
        /\/about-us\/signature-ruby/,
        /\/about-us\/free-worldwide-shipping/,
        /\/about-us\/packaging/,
        /\/about-us\/faqs/,
        /\/about-us\/our-story/,
        /\/privacy/,
        /\/terms/,
        /\/cookies/,
        /\/returns-policy/,
        /\/proposal-rings/,
        /\/custom-engagement-ring-design/,
        /\/aftercare-service-request-thank-you/,
        /\/home-2/,
    ]
    const releasedUrlsNext: RegExp[] = []

    const isStagingNext = host.host === 'staging-next.coderings.com'

    const isReleasedUrl = installReleasedUrls(
        app,
        {
            releasedRoutes: [
                ...releasedRoutes,
                ...(isStagingNext ? releasedRoutesNext : []),
            ],
            releasedPages: [
                ...releasedUrls,
                ...(isStagingNext ? releasedUrlsNext : []),
            ],
        },
        host
    )

    const routerNavigation = installRouterNavigation(app, router, isReleasedUrl)

    const { setAuth, user } = auth
    installAnalytics(app, analyticsId, user)

    const ssrExchange = createSSRExchange({
        isClient: !import.meta.env.SSR,
        initialState: initialState?.api,
        staleWhileRevalidate: false,
    })

    const acceptLanguageExchange = addHeaderExchange(() => ({
        'Accept-Language': locale.value.split('_')[0] ?? 'en',
    }))

    if (QA) {
        const getKey = (binding: DirectiveBinding): string => {
            const modifiers = Object.keys(binding.modifiers)

            if (modifiers.length) {
                return `data-qa-${modifiers.join('-')}`
            }

            return 'data-qa'
        }

        const handle = (el: HTMLElement, binding: DirectiveBinding): void => {
            el.setAttribute(getKey(binding), binding.value)
            if (binding.arg) {
                el.setAttribute(`data-qa`, binding.arg)
            }
        }

        app.directive('qa', {
            created(el: HTMLElement, binding) {
                handle(el, binding)
            },
            updated(el, binding) {
                handle(el, binding)
            },
            getSSRProps(binding) {
                if (qaIsNoJs) {
                    return {
                        [getKey(binding)]: binding.value,
                    }
                }

                return undefined
            },
        })
    } else {
        app.directive('qa', {})
    }

    // eslint-disable-next-line vue-composable/composable-placement
    const amazonTraceId = useGtmVar('amazon-trace-id', '')

    const amazonTraceIdExchange = mapExchange({
        onResult(result) {
            const id = result.extensions?.['amazonTraceId']

            if (id) {
                amazonTraceId.value = id
            }

            return result
        },
    })

    const marketingCloudFormExchange = mapExchange({
        onResult(result) {
            const forms = result.extensions?.['marketingCloudForms'] as
                | string[]
                | undefined

            if (forms) {
                forms.forEach((item) => {
                    trackGtmEvent('WebsiteFormSubmit', {
                        FormSubmit: item,
                    })
                })
            }

            return result
        },
    })

    const keys: Required<GraphCacheKeysConfig> = {
        BookingConsultationPurposeOrder: () => null,
        Review: (data) => data.id || null,
        ReviewsAggregate: () => null,
        SquareTextBox: () => null,
        SquareImageBox: () => null,
        ComponentSquareBoxes: () => null,
        ComponentShopWithConfidence: () => null,
        ShopWithConfidenceEthnicallySourcedDiamonds: () => null,
        ShopWithConfidenceFinancingOptions: () => null,
        ShopWithConfidenceGreenlandRuby: () => null,
        ShopWithConfidenceHandFinished: () => null,
        ShopWithConfidenceResize: () => null,
        ShopWithConfidenceReturnPolicy: () => null,
        ShopWithConfidenceShipping: () => null,
        CatalogueEngagementRingFiltersVariation: () => null,
        CatalogueJewelleriesBraceletsFiltersVariation: () => null,
        CatalogueJewelleriesEarringsFiltersVariation: () => null,
        CatalogueJewelleriesFiltersVariation: () => null,
        CatalogueJewelleriesNecklacesFiltersVariation: () => null,
        CatalogueWeddingRingFiltersVariation: () => null,
        ComponentTitleAmpersand: () => null,
        ComponentTitleText: () => null,
        PhoneNumberType: (data) => data.e164 || null,
        CatalogueVideo: (data) => data.uri || null,
        ComponentGalleryWithEnquiry: () => null,
        GalleryWithEnquiryCard: () => null,
        GemsApi_StoneCrystalInfo: (data) => data.code || null,
        LinkItemAffirm: () => null,
        MonolithImageSize: () => null,
        CatalogueImage: (data) => data.id ?? null,
        GenericImage: (data) => data.url ?? null,
        ImageSrc: () => null,
        PostsAggregation: () => null,
        AuthorsAggregation: () => null,
        PostTagsAggregation: () => null,
        PostCategoriesAggregation: () => null,
        RingBuilderMountFragment: (data) => data.id ?? null,
        RingBuilderCentreStoneFragment: (data) => data.id ?? null,
        StoneRecommendationsResponseSuccess: () => null,
        StoneRecommendationsResponseFailure: () => null,
        StoneRecommendationsPriceRange: () => null,
        EngagementRingStoneRecommendations: () => null,
        EngagementRingStoneRecommendationNaturalDiamonds: () => null,
        EngagementRingStoneRecommendationNonSkuGemStoneCard: () => null,
        EngagementRingStoneRecommendationNonSkuLabGrown: () => null,
        StoneRecommendationNonSkuGemstone: () => null,
        Banner1: () => null,
        Banner2: () => null,
        BookingConsultationAvailableDate: (data) => data.date || null,
        BookingInPersonConsultationRoom: (data) => data.id || null,
        BookingConsultationAvailableShowroom: () => null,
        BookingVirtualConsultationRoom: (data) => data.id || null,
        Region: (data) => data.code || null,
        Block: () => null,
        Money: () => null,
        StoneClarity: (data) => data.systemCode || null,
        StoneClarityTranslation: () => null,
        StoneColor: (data) => data.systemCode || null,
        StoneColorTranslation: () => null,
        StoneCrystal: (data) => data.systemCode || null,
        StoneCrystalTranslation: () => null,
        EngagementRingStoneRecommendationCard: () => null,
        CatalogueMelee: () => null,
        AvatarStackItem: () => null,
        CatalogueEngagementRingCentreStoneWeightConstraint: () => null,
        GemsApi_Stone: (data) => data.identifier || null,
        StoneCategory: (data) => data.systemCode || null,
        StoneCategoryTranslation: () => null,
        GemsApi_StoneCategoryInfo: (data) => data.code || null,
        GemsApi_StoneClarityInfo: (data) => data.code || null,
        GemsApi_StoneColorInfo: (data) => data.code || null,
        GemsApi_StoneCutInfo: (data) => data.code || null,
        StoneCut: (data) => data.systemCode || null,
        StoneCutTranslation: () => null,
        GemsApi_StoneFluorescenceInfo: (data) => data.code || null,
        StoneFluorescenceTranslation: () => null,
        StoneFluorescence: (data) => data.systemCode || null,
        GemsApi_StoneGradeInfo: (data) => data.code || null,
        StoneGradeTranslation: () => null,
        StoneGrade: (data) => data.systemCode || null,
        GemsApi_StonePolishInfo: (data) => data.code || null,
        GemsApi_StoneShapeInfo: (data) => data.code || null,
        StoneShapeTranslation: () => null,
        StoneShape: (data) => data.systemCode || null,
        GemsApi_StoneSymmetryInfo: (data) => data.code || null,
        StoneSymmetryTranslation: () => null,
        StoneSymmetry: (data) => data.systemCode || null,
        GemsApi_StoneImage: () => null,
        GemsApi_StoneCertificates: () => null,
        GemsApi_PricePoint: () => null,
        GemsApiPriceBounds: () => null,
        FooterLogo: () => null,
        SubMenuItem: () => null,
        SubMenu: () => null,
        SubMenuSection: () => null,
        FooterColumnItemLink: () => null,
        FooterColumnItemLinkList: () => null,
        FooterColumnItemSeparator: () => null,
        FooterColumnItemTitle: () => null,
        FooterColumnItemWorkingHours: () => null,
        FooterColumn: () => null,
        WorkingHours: () => null,
        PaymentIcon: () => null,
        Footer: () => null,
        SocialNetwork: () => null,
        MenuItem: () => null,
        Translation: (data) => data.token || null,
        Language: (data) => data.locale || null,
        Meta: () => null,
        Logo: () => null,
        Link: () => null,
        LinkItemCustom: () => null,
        LinkItemOpenContactForm: () => null,
        LinkItemOpenSpeakToExpertModal: () => null,
        LinkItemOpenEnquiryForm: () => null,
        LinkItemBookingForm: () => null,
        ComponentLogos: () => null,
        ComponentInstagramPosts: () => null,
        ComponentInstagramWithContent: () => null,
        InstagramPost: () => null,
        ComponentTitle: () => null,
        CardCarouselCard: () => null,
        ComponentCardCarousel: () => null,
        ComponentContent: () => null,
        ComponentRoundedImage: () => null,
        ButtonStyle: () => null,
        ComponentButton: () => null,
        ComponentText: () => null,
        ComponentGlobalSpacer: () => null,
        ComponentTextWithImage: () => null,
        ComponentTextWithImageCarousel: () => null,
        ComponentFullWidthImage: () => null,
        ComponentGlobalComponent: () => null,
        ComponentRoundedImageContent: () => null,
        ReviewCarouselCard: () => null,
        ComponentReviewCarousel: () => null,
        GalleryCarouselCard: () => null,
        ComponentGalleryCarousel: () => null,
        ComponentSpacer: () => null,
        ComponentVideo: () => null,
        ComponentVideoWithContent: () => null,
        ButtonIcon: () => null,
        ComponentTextOverImage: () => null,
        ComponentSideBySideImage: () => null,
        ComponentSideBySideImages: () => null,
        ComponentBannerSkinny: () => null,
        ComponentQuote: () => null,
        ComponentCard: () => null,
        CatalogueFilterMetalItem: () => null,
        CatalogueFilterShapeItem: () => null,
        CatalogueEngagementRingCentreStoneConstraints: () => null,
        CataloguePriceRange: () => null,
        MetalElement: (data) => data.systemCode || null,
        MetalColor: (data) => data.systemCode || null,
        MetalColorTranslation: () => null,
        MetalTranslation: () => null,
        ComponentMainCover: () => null,
        CatalogueEngagementRingTranslation: () => null,
        CatalogueEngagementRingFilters: () => null,
        CataloguePriceRangeFilter: () => null,
        // todo with this ER+NonSKU goes to shit
        // CatalogueEngagementRing: (data) => data.catalogueCode ?? null,
        CatalogueEngagementRing: () => null,
        AffirmFinanceOption: () => null,
        KlarnaFinanceOption: () => null,
        AddToCartReadyToGoRingSuccessExistingCustomer: () => null,
        AddToCartReadyToGoRingSuccessNewCustomer: () => null,
        Address: () => null,
        AffirmCompleteTransactionResponse: () => null,
        AffirmPaymentMethod: (data) => data.systemCode ?? null,
        AffirmPaymentResponse: () => null,
        AffirmTransaction: (data) => data.id ?? null,
        AllocationPaymentMethod: (data) => data.systemCode ?? null,
        AllocationTransaction: (data) => data.id ?? null,
        ArticleGridItem: () => null,
        ArticlePreview: () => null,
        Asset: (data) => data.id ?? null,
        Author: (data) => data.id ?? null,
        AvataxAddressInvalidError: () => null,
        BankPaymentMethod: (data) => data.systemCode ?? null,
        BankTransaction: (data) => data.id ?? null,
        BlockAnchor: () => null,
        BookingConsultation: (data) => data.id ?? null,
        BookingConsultationComplexity: (data) => data.systemCode ?? null,
        BookingConsultationComplexityTranslation: () => null,
        BookingConsultationMethod: (data) => data.systemCode ?? null,
        BookingConsultationPurpose: (data) => data.systemCode ?? null,
        BookingConsultationSlot: () => null,
        BookingConsultationSlotsResponse: () => null,
        BookingConsultationType: (data) => data.systemCode ?? null,
        BookingCreateConsultationErrorNoAvailableConsultants: () => null,
        BookingCreateConsultationSuccessExistingCustomer: () => null,
        BookingCreateConsultationSuccessNewCustomer: () => null,
        SearchEverywhereResponse: () => null,
        ButtonEventTracking: () => null,
        CatalogueAggregate: () => null,
        CatalogueBandType: (data) => data.id ?? null,
        CatalogueBandTypeTranslation: () => null,
        CatalogueDesignStudioCheckoutEngagementRingAddedToDesignStudio: () =>
            null,
        CatalogueDesignStudioCheckoutEngagementRingAddedToDesignStudioAndCart:
            () => null,
        CatalogueDesignStudioCheckoutWeddingRingSuccessExistingCustomer: () =>
            null,
        CatalogueDesignStudioCheckoutWeddingRingSuccessNewCustomer: () => null,
        CatalogueFilterWeddingRingCategoriesItem: () => null,
        CatalogueEngagementRingSignatureStone: (data) => data.id ?? null,
        CatalogueEngagementRingAndSignatureStone: () => null,
        CatalogueFilterJewelleryBraceletProductStyleLabel: () => null,
        CatalogueFilterJewelleryRingProductStyleLabel: () => null,
        CatalogueJewelleriesRingsFiltersVariation: () => null,
        CatalogueJewelleryRing: (data) => data.catalogueCode ?? null,
        CatalogueJewelleryRingTranslation: () => null,
        CatalogueJewelleryRingsFilters: () => null,
        JewelleryRingProductStyleLabel: () => null,
        CatalogueFilterJewelleryDesignFeatureLabel: () => null,
        CatalogueFilterJewelleryDesignStyleLabel: () => null,
        CatalogueFilterJewelleryEarringsProductStyleLabel: () => null,
        CatalogueFilterJewelleryNecklaceProductStyleLabel: () => null,
        CatalogueFilterJewelleryGemstoneLabel: () => null,
        JewelleryGemstoneLabel: (data) => data.systemCode ?? null,
        CatalogueJewelleriesFilters: () => null,
        CatalogueJewelleryBracelet: (data) => data.catalogueCode ?? null,
        CatalogueJewelleryBraceletsFilters: () => null,
        CatalogueJewelleryEarrings: (data) => data.catalogueCode ?? null,
        CatalogueJewelleryEarringsFilters: () => null,
        CatalogueJewelleryNecklace: (data) => data.catalogueCode ?? null,
        CatalogueJewelleryNecklacesFilters: () => null,
        JewelleryBraceletProductStyleLabel: (data) => data.systemCode ?? null,
        JewelleryDesignFeatureLabel: (data) => data.systemCode ?? null,
        JewelleryDesignStyleLabel: (data) => data.systemCode ?? null,
        JewelleryEarringsProductStyleLabel: (data) => data.systemCode ?? null,
        JewelleryNecklaceProductStyleLabel: (data) => data.systemCode ?? null,
        CatalogueJewelleryBraceletTranslation: () => null,
        CatalogueJewelleryEarringsTranslation: () => null,
        CatalogueJewelleryNecklaceTranslation: () => null,
        CatalogueDesignStudioCheckoutJewellerySuccessExistingCustomer: () =>
            null,
        CatalogueDesignStudioCheckoutJewellerySuccessNewCustomer: () => null,
        CataloguePriceRangeBar: () => null,
        CatalogueReadyToGoRing: (data) => data.catalogueCode ?? null,
        CatalogueReadyToGoRingFilters: () => null,
        CatalogueReadyToGoRingTranslation: () => null,
        CatalogueWeddingRing: (data) => data.catalogueCode ?? null,
        CatalogueWeddingRingFilters: () => null,
        CatalogueWeddingRingTranslation: () => null,
        CheckoutcomOperationDeclinedError: () => null,
        CheckoutcomPaymentMethod: (data) => data.systemCode ?? null,
        CheckoutcomTransaction: (data) => data.id ?? null,
        CompleteCheckoutcomTransactionPost3dsFailureResponse: () => null,
        CompleteCheckoutcomTransactionPost3dsSuccessResponse: () => null,
        ComponentArticleGrid: () => null,
        ComponentArticlePreviews: () => null,
        ComponentBannerTitle: () => null,
        ComponentCatalogueProductCarousel: () => null,
        ComponentCatalogueProductCarouselItem: () => null,
        ComponentContentTwoColumns: () => null,
        ComponentCustomProductCarousel: () => null,
        ComponentFaq: () => null,
        ComponentIconCarousel: () => null,
        ComponentImage: () => null,
        ComponentLogosCarousel: () => null,
        ComponentOnThisPage: () => null,
        ComponentOrderedList: () => null,
        ComponentTable: () => null,
        Consultant: (data) => data.id ?? null,
        CreateCheckoutcomTransactionFailureResponse: () => null,
        CreateCheckoutcomTransactionSuccessResponse: () => null,
        Customer: (data) => data.id ?? null,
        CustomerRegistrationResponse: () => null,
        DeliveryDate: () => null,
        DesignInspiration: (data) => data.id ?? null,
        DesignStudioEngagementRingCentreStone: (data) => data.id ?? null,
        DesignStudioEngagementRingErrorGeneric: () => null,
        DesignStudioEngagementRingErrorMaxCaratWeight: () => null,
        DesignStudioEngagementRingErrorMinCaratWeight: () => null,
        DesignStudioEngagementRingErrorShapeMismatch: () => null,
        DesignStudioEngagementRingFailure: () => null,
        DesignStudioEngagementRingMount: (data) => data.id ?? null,
        DesignStudioExternalCentreStone: (data) => data.id ?? null,
        DesignStudioExternalSideStone: (data) => data.id ?? null,
        DesignStudioInternalCentreStone: (data) => data.id ?? null,
        DesignStudioInternalSideStone: (data) => data.id ?? null,
        DesignStudioMeleeItem: (data) => data.id ?? null,
        DesignStudioProductEngagementRing: (data) => data.id ?? null,
        DesignStudioProductWeddingRing: (data) => data.id ?? null,
        DesignStudioProductLooseStone: (data) => data.id ?? null,
        DesignStudioProductAdditionalResize: (data) => data.id ?? null,
        DesignStudioProductCustom: (data) => data.id ?? null,
        DesignStudioProductGeneric: (data) => data.id ?? null,
        DesignStudioSignatureStone: (data) => data.id ?? null,
        DesiredDeliveryDateInvalidError: () => null,
        EnquiryConsultation: (data) => data.id ?? null,
        EnquiryEngagementRingAddToCart: () => null,
        EnquiryResponseExistingCustomer: () => null,
        EnquiryResponseNewCustomer: () => null,
        EnquiryWeddingRingAddToCart: () => null,
        FaqQuestion: () => null,
        FaqSection: () => null,
        FooterColumnItemText: () => null,
        GalleryItem: () => null,
        GemsApiStone: (data) => data.identifier ?? null,
        GemsApi_Bounds: () => null,
        GemsApi_Pagination: () => null,
        GemsApi_PriceRange: () => null,
        GemsApi_Range: () => null,
        GemsApi_IntRange: () => null,
        GemsApi_PriceRangeByShape: () => null,
        GemsApi_SelectedRange: () => null,
        GemsApi_SelectedSorting: () => null,
        GemsApi_SelectedStoneFilters: () => null,
        GemsApi_StoneCollection: () => null,
        GemsApi_dtlReport: () => null,
        HistoricalNonSkuGemstone: () => null,
        HistoricalNonSkuLabGrown: () => null,
        IconCarouselItem: () => null,
        Image: (data) => data.id ?? null,
        ImageSize: () => null,
        InvalidateCacheResult: () => null,
        InvalidateCacheResultError: () => null,
        InvalidateCacheResultSuccess: () => null,
        KlarnaPaymentMethod: (data) => data.systemCode ?? null,
        KlarnaPaymentResponse: () => null,
        KlarnaTransaction: (data) => data.id ?? null,
        LegacyTransaction: (data) => data.id ?? null,
        LoginResponseFailure: () => null,
        LoginResponseSuccess: () => null,
        MagicLinkConsumeError: () => null,
        MagicLinkConsumeSuccess: () => null,
        MarketingDataResponse: () => null,
        Melee: (data) => data.id ?? null,
        Metal: () => null,
        MetalElementTranslation: () => null,
        NoPaymentMethod: (data) => data.systemCode ?? null,
        NonSkuCaratWeightRange: () => null,
        NonSkuGemstone: (data) => data.id ?? null,
        NonSkuGemstoneType: (data) => data.id ?? null,
        NonSkuGemstonesBounds: () => null,
        NonSkuLabGrown: (data) => data.id ?? null,
        NonSkuLabGrownDiamondsBounds: () => null,
        NonSkuPriceRange: () => null,
        Order: (data) => data.id ?? null,
        OrderAddress: (data) => data.id ?? null,
        OrderAddressNotConfirmedError: () => null,
        OrderAlreadyCompletedError: () => null,
        OrderEmptyError: () => null,
        OrderProduct: (data) => data.id ?? null,
        OrderStatus: (data) => data.systemCode || null,
        OrderedListItem: () => null,
        Page: (data) => data.id ?? null,
        PageSettings: () => null,
        PageTooltip: () => null,
        PayViaCheckoutcomFailureResponse: () => null,
        PayViaCheckoutcomSuccessResponse: () => null,
        PaymentAmountInvalidError: () => null,
        PaymentMethodContainer: () => null,
        PaymentMethodNotAllowedError: () => null,
        PaymentPlan: () => null,
        PaymentPlanPaidRecord: () => null,
        PaymentPlanUpcomingRecord: () => null,
        Post: (data) => data.id ?? null,
        PostCategory: (data) => data.id ?? null,
        PostTag: (data) => data.id ?? null,
        PreHeader: () => null,
        ProductLabelTranslation: () => null,
        ProductPageDetails: (data) => data.id ?? null,
        ProductPageListing: (data) => data.id ?? null,
        ProductPageFacetedContentLongDescriptionBlock: () => null,
        ProductPageFacetedContentLongDescriptionText: () => null,
        ProductPageFacetedContent: (data) => data.productString || null,
        PromoCode: (data) => data.id ?? null,
        PromoCodeApplicationResult: () => null,
        Questionnaire: (data) => data.id ?? null,
        QuestionnaireCompletedExistingCustomerResponse: () => null,
        QuestionnaireCompletedNewCustomerResponse: () => null,
        QuestionnaireCompletedQuestionnaire: () => null,
        QuestionnaireCompletedQuestionnaireStepSnapshot: () => null,
        QuestionnaireCondition: () => null,
        QuestionnaireConditionEqualToChoice: () => null,
        QuestionnaireConditionIsEmpty: () => null,
        QuestionnaireConditionIsEqualTo: () => null,
        QuestionnaireConditionIsGreaterThan: () => null,
        QuestionnaireConditionIsLessThan: () => null,
        QuestionnaireConditionIsNotEmpty: () => null,
        QuestionnaireConditionNotIsEqualTo: () => null,
        QuestionnaireCustomerNotification: () => null,
        QuestionnaireSubmissionNotification: () => null,
        QuestionnaireQuestionAnswerMultiChoice: (data) => data.id ?? null,
        QuestionnaireQuestionAnswerNumber: (data) => data.id ?? null,
        QuestionnaireQuestionAnswerSingleChoice: (data) => data.id ?? null,
        QuestionnaireQuestionAnswerText: (data) => data.id ?? null,
        QuestionnaireQuestionChoice: (data) => data.id ?? null,
        QuestionnaireQuestionChoiceOption: (data) => data.id ?? null,
        QuestionnaireQuestionChoiceOptionContentIconCard: () => null,
        QuestionnaireQuestionChoiceOptionContentIconCardConditional: () => null,
        QuestionnaireQuestionChoiceOptionContentImage: () => null,
        QuestionnaireQuestionChoiceOptionContentImageCard: () => null,
        QuestionnaireQuestionChoiceOptionContentImageCardConditional: () =>
            null,
        QuestionnaireQuestionChoiceOptionContentImageConditional: () => null,
        QuestionnaireQuestionChoiceOptionContentText: () => null,
        QuestionnaireQuestionChoiceOptionContentTextConditional: () => null,
        QuestionnaireQuestionChoiceOptionLayoutSnapshotIconCard: () => null,
        QuestionnaireQuestionChoiceOptionLayoutSnapshotImage: () => null,
        QuestionnaireQuestionChoiceOptionLayoutSnapshotImageCard: () => null,
        QuestionnaireQuestionChoiceOptionLayoutSnapshotText: () => null,
        QuestionnaireQuestionChoiceOptionSnapshot: () => null,
        QuestionnaireQuestionChoiceOptionBehaviour: () => null,
        QuestionnaireQuestionPrice: () => null,
        QuestionnaireQuestionSnapshot: () => null,
        QuestionnaireQuestionText: () => null,
        QuestionnaireSignUpStep: () => null,
        QuestionnaireStep: () => null,
        QuestionnaireStepContentContent: () => null,
        QuestionnaireStepContentQuestions: () => null,
        QuotationEngagementRing: (data) => data.id ?? null,
        QuotationManualSurcharge: (data) => data.id ?? null,
        QuotationPercentageSurcharge: (data) => data.id ?? null,
        QuotationWeddingRing: (data) => data.id ?? null,
        RecommendedRingSize: (data) => data.id ?? null,
        RingBuilder: (data) => data.id ?? null,
        RingBuilderResolvedExistingCustomerSuccess: () => null,
        RingBuilderSetResult: () => null,
        RingBuilderResolvedFailure: () => null,
        RingBuilderResolvedNewCustomerSuccess: () => null,
        RingSize: (data) => data.id ?? null,
        SetDeliveryInformationFailureResponse: () => null,
        SetDeliveryInformationSuccessResponse: () => null,
        ShipmentTrackingInformation: () => null,
        Showroom: (data) => data.id ?? null,
        ShowroomLocation: (data) => data.id ?? null,
        ShowroomTranslation: () => null,
        Stone: (data) => data.id ?? null,
        StoneCertificate: () => null,
        StoneMeasurements: () => null,
        StonePolish: (data) => data.systemCode ?? null,
        StonePolishTranslation: () => null,
        StoneRecommendationNotFound: () => null,
        StoneRecommendationsNaturalDiamondFilters: () => null,
        StoneRecommendationsPercentRange: () => null,
        StripeTransaction: (data) => data.id ?? null,
        SubMenuEditorialContent: () => null,
        Table: () => null,
        TableBody: () => null,
        TableHeader: () => null,
        TableRow: () => null,
        Tooltip: () => null,
        TopBar: () => null,
        TopBarConsultationPrompt: () => null,
        TopBarSiteWideMessage: () => null,
        TransactionStatus: (data) => data.systemCode ?? null,
        User: (data) => data.id ?? null,
        UserCustomerEngagementRingRecommendations: () => null,
        Video: (data) => data.id ?? null,
        WeddingRingCategoryLabel: (data) => data.systemCode ?? null,
        CarouselCustomProduct: () => null,
        ComponentContentWithGallery: () => null,
        GeoPoint: (data) => data.id ?? null,
        ComponentRelatedProducts: () => null,
        ComponentTextCarousel: () => null,
        TextCarouselItem: () => null,
        Tax: () => null,
        SalePrice: () => null,
        CatalogueFilterEngagementRingDesignFeatureItem: () => null,
        WeddingRingStyleLabel: (data) => data.systemCode || null,
        CatalogueFilterEngagementRingDesingStyleItem: () => null,
        CatalogueFilterEngagementRingStylesItem: () => null,
        CatalogueFilterWeddingRingStylesItem: () => null,
        EngagementRingStyleLabel: (data) => data.systemCode || null,
        EngagementRingDesignFeatureLabel: (data) => data.systemCode || null,
        EngagementRingDesignStyleLabel: (data) => data.systemCode || null,
        NonSkuGemstoneCaratDimension: (data) => data.id || null,
        NonSkuGemstoneGradeClarityDimension: (data) => data.id || null,
        NonSkuGemstoneImage: (data) => data.id || null,
        NonSkuGemstoneShapeDimension: (data) => data.id || null,
        NonSkuImage: (data) => data.id || null,
        NonSkuLabGrownCaratDimension: (data) => data.id || null,
        NonSkuLabGrownGradeDimension: (data) => data.id || null,
        NonSkuLabGrownShapeDimension: (data) => data.id || null,
    }

    const authExchange = mapExchange({
        onError: (error) => {
            if (
                error.graphQLErrors.some(
                    (error) => error.extensions['category'] === 'authorization'
                )
            ) {
                setAuth(null)
            }
        },
    })

    const ignoredExtensions = ['validation', 'authorization']
    const sentryExchange = mapExchange({
        onError: (error) => {
            error.graphQLErrors.forEach((error) => {
                if (
                    ignoredExtensions.includes(
                        error.extensions['category'] as string
                    )
                ) {
                    return
                }

                Sentry.captureMessage(error.message, (scope) => {
                    scope.setExtras(error.extensions)

                    return scope
                })
            })

            if (error.networkError) {
                Sentry.captureException(error.networkError, (scope) => {
                    if (error.response instanceof Response) {
                        scope.setExtra('response', {
                            status: error.response.status,
                            statusText: error.response.status,
                            headers: error.response.headers,
                        })
                    } else {
                        scope.setExtra('response', error.response)
                    }

                    return scope
                })
            }
        },
    })

    const createApiClient = () => {
        return createClient({
            url: urls.api,
            requestPolicy: 'cache-and-network',
            fetch: async (...args) => {
                return fetch(...args).then((res) => {
                    if (
                        res.status >= 400 &&
                        res.status < 500 &&
                        res.status !== 404
                    ) {
                        // eslint-disable-next-line no-console
                        console.error(args, res)
                    }

                    return res
                })
            },
            fetchOptions: {
                credentials: 'include',
                headers: {
                    'accept': 'application/json',
                },
            },
            exchanges: [
                // debugExchange,
                // devtoolsExchange,
                ...(import.meta.env.SSR
                    ? []
                    : [
                          cacheExchange<Partial<GraphCacheConfig>>({
                              schema,
                              keys,
                          }),
                      ]),
                amazonTraceIdExchange,
                marketingCloudFormExchange,
                ...(sentryEnabled ? [sentryExchange] : []),
                authExchange,
                acceptLanguageExchange,
                ...exchanges,
                ssrExchange,
                persistedExchange({
                    // preferGetForPersistedQueries: true, WORKS ONLY WITH ABSOLUTE URLS
                }),
                fetchExchange,
            ],
        })
    }

    const graphqlClient = ref(createApiClient())
    provideResetApiClient(app, () => {
        graphqlClient.value = createApiClient()
    })

    install(app, graphqlClient)

    const ringBuilderSetMutation: SimpleMutationResponse<
        RingBuilderSetMutation,
        RingBuilderSetMutationVariables
    > = adaptMutation(graphqlClient, RingBuilderSetDocument)

    const ringBuilderCookieStore = installRingBuilderStore(app, cookieProvider)

    installRingBuilder(app, {
        translator: appTranslator,
        currency: currencyInput,
        regionPlugin,
        formatMoney: moneyFormatterNew,
        routes,
        routerNavigation,
        ringBuilderDataStore,
        ringBuilderCookieStore,
        ringBuilderSetMutation,
    })

    return {
        app,
        graphqlClient,
        router,
        head,
        i18n,
        locale,
        ssrExchange,
        absoluteUrlGenerator,
        isReleasedUrl,
        pinia,
        routes,
        robots,
    }
}
